import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  emailAdminApi,
  type GetTemplatesParams,
  type CreateTemplatePayload,
  type UpdateTemplatePayload,
  type GetCampaignsParams,
  type CreateCampaignPayload,
  type UpdateCampaignPayload,
  type GetEmailHistoryParams,
  type SendBulkEmailPayload,
} from '../api/emailAdminApi'
import { useToast } from '@/hooks/use-toast'

// ==========================================
// Email Templates Hooks
// ==========================================

export function useEmailTemplates(params: GetTemplatesParams = {}) {
  return useQuery({
    queryKey: ['emailTemplates', params],
    queryFn: () => emailAdminApi.getTemplates(params),
  })
}

export function useEmailTemplate(templateId: number | null) {
  return useQuery({
    queryKey: ['emailTemplate', templateId],
    queryFn: () => emailAdminApi.getTemplate(templateId!),
    enabled: !!templateId,
  })
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (payload: CreateTemplatePayload) => emailAdminApi.createTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
      toast({
        title: 'Template created',
        description: 'Email template has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create template',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ templateId, payload }: { templateId: number; payload: UpdateTemplatePayload }) =>
      emailAdminApi.updateTemplate(templateId, payload),
    onSuccess: (_, { templateId }) => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', templateId] })
      toast({
        title: 'Template updated',
        description: 'Email template has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update template',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (templateId: number) => emailAdminApi.deleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
      toast({
        title: 'Template deleted',
        description: 'Email template has been deleted.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete template',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// ==========================================
// Email Campaigns Hooks
// ==========================================

export function useEmailCampaigns(params: GetCampaignsParams = {}) {
  return useQuery({
    queryKey: ['emailCampaigns', params],
    queryFn: () => emailAdminApi.getCampaigns(params),
  })
}

export function useEmailCampaign(campaignId: number | null) {
  return useQuery({
    queryKey: ['emailCampaign', campaignId],
    queryFn: () => emailAdminApi.getCampaign(campaignId!),
    enabled: !!campaignId,
  })
}

export function useCreateEmailCampaign() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (payload: CreateCampaignPayload) => emailAdminApi.createCampaign(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] })
      toast({
        title: 'Campaign created',
        description: 'Email campaign has been created successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create campaign',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateEmailCampaign() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ campaignId, payload }: { campaignId: number; payload: UpdateCampaignPayload }) =>
      emailAdminApi.updateCampaign(campaignId, payload),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['emailCampaign', campaignId] })
      toast({
        title: 'Campaign updated',
        description: 'Email campaign has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update campaign',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteEmailCampaign() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (campaignId: number) => emailAdminApi.deleteCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] })
      toast({
        title: 'Campaign deleted',
        description: 'Email campaign has been deleted.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete campaign',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

export function useSendEmailCampaign() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (campaignId: number) => emailAdminApi.sendCampaign(campaignId),
    onSuccess: (data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['emailCampaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['emailHistory'] })
      queryClient.invalidateQueries({ queryKey: ['emailStats'] })
      toast({
        title: 'Campaign sent',
        description: data.message,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send campaign',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}

// ==========================================
// Email History & Stats Hooks
// ==========================================

export function useEmailHistory(params: GetEmailHistoryParams = {}) {
  return useQuery({
    queryKey: ['emailHistory', params],
    queryFn: () => emailAdminApi.getEmailHistory(params),
  })
}

export function useEmailStats() {
  return useQuery({
    queryKey: ['emailStats'],
    queryFn: () => emailAdminApi.getEmailStats(),
  })
}

export function useSendBulkEmail() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (payload: SendBulkEmailPayload) => emailAdminApi.sendBulkEmail(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['emailHistory'] })
      queryClient.invalidateQueries({ queryKey: ['emailStats'] })
      queryClient.invalidateQueries({ queryKey: ['artists'] })
      toast({
        title: 'Bulk email sent',
        description: data.message,
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to send bulk email',
        description: error.message,
        variant: 'destructive',
      })
    },
  })
}
